import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { initiateVirtualCardCreation } from '../../../../api/api';

import CardsProgressSteps from '../../../../components/ProgressStep/CardsProgressSteps';
import chooseCardIcon from '../../../../assets/img/icon/card_icon3.png';
import visaCardLogo from '../../../../assets/img/logo/visa-card-logo.png';
import brandLogo from '../../../../assets/img/logo/cardplot_card_logo.png';
import brandLogo2 from '../../../../assets/img/logo/favicon.png';
import masterCardBgImg from '../../../../assets/img/logo/card_bg-cardplot.png';
import { FaAngleLeft } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { formatLabel, getFormattedAmount } from '../../../../actions/payments';

const CardReview = ({ userDetails }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const storedCardDetails = location.state?.customizedCardDetails;
    const amountFunded = location.state?.amountFunded;
    const validationData = location.state?.validationData || {};
    const creationPayload = location.state?.creationPayload;

    const cardDetails = {
        cardholderName: validationData?.virtual_card?.card_name || userDetails?.bank_account[0]?.account_name,
        nickname: storedCardDetails?.nickname,
        cardNumber: validationData?.virtual_card?.card_masked_pan || "0000 **** **** 0000",
        expiryDate: "00/00",
        fundingAmount: validationData?.card_event_amount?.amount ? `$${validationData.card_event_amount.amount}` : `$${(amountFunded)?.toFixed(2)}`,
        cardLogo: storedCardDetails?.cardTypeLogo,
        backgroundColor: storedCardDetails?.cardTheme?.color,
    };

    const walletCurrency = userDetails?.default_wallet?.currency_code || 'NGN';
    const formatWalletCurrency = (value) => new Intl.NumberFormat(
        walletCurrency === "NGN" ? "en-NG" : "en-US", {
        style: "currency",
        currency: walletCurrency,
        minimumFractionDigits: 2,
    }).format(value);

    const walletBalance = Number(
        String(userDetails?.default_wallet?.balance || 0).replace(/[^0-9.-]+/g, "")
    ) || 0;

    const getNestedValue = (key) => {
        return validationData?.[key] ?? validationData?.card_event?.[key];
    };

    const getMaskedCardNumber = () => {
        const rawNum = getNestedValue('card_masked_pan') || validationData?.virtual_card?.card_masked_pan || cardDetails.cardNumber;
        if (rawNum.includes("*") || rawNum.includes(" ")) return rawNum;
        const digits = String(rawNum).replace(/\D/g, "");
        const lastFour = digits.slice(-4);
        return lastFour ? `**** **** **** ${lastFour}` : "**** **** ****";
    };

    const getExchangeRateDisplay = () => {
        const val = getNestedValue('exchange_rate_info');
        if (!val) return null;
        if (typeof val === 'string') return val;
        if (val.description) return val.description;
        const currency = val.wallet_currency || val.currency || val.currency_code;
        const rate = val.rate;
        if (currency && rate !== undefined) {
            const rateStr = String(rate);
            if (rateStr.includes("$1") || rateStr.includes("=")) return rateStr;
            return `$1 = ${currency}${rateStr}`;
        }
        const amount = val.wallet_amount ?? val.total_amount ?? val.amount;
        if (currency && amount !== undefined) {
            return `${currency} ${amount}`;
        }
        return null;
    };



    const handleGoBack = () => {
        navigate("/user/cards/billing_address", { 
            state: { 
                customizedCardDetails: storedCardDetails,
                amountFunded: amountFunded,
                creationPayload: creationPayload
            },
            replace: true 
        }); 
    }

    const [loading, setLoading] = useState(false);

    const handleContinue = async () => {
        setLoading(true);
        try {
            if (!creationPayload) {
                throw new Error("The validated card creation request is missing. Please validate again.");
            }
            await initiateVirtualCardCreation(creationPayload);
            
            toast.success("Your card was created successfully", {
                toastId: "funding-success-toast",
                className: "custom-toast",
            }); 
            setTimeout(() => {
                navigate("/user/cards");
            }, 1500);
        } catch (error) {
            let errorMsg = "Failed to create card. Please try again.";
            if (error.response?.data?.message) {
                errorMsg = error.response.data.message;
                
                // Show specific validation errors if present
                if (error.response.data.errors) {
                    const firstErrorKey = Object.keys(error.response.data.errors)[0];
                    if (firstErrorKey) {
                        errorMsg = error.response.data.errors[firstErrorKey][0];
                    }
                }
            }
            toast.error(errorMsg, {
                toastId: "funding-error-toast",
                className: "custom-toast",
            });
        } finally {
            setLoading(false);
        }
    }

  return (
    <section className='create__card__page card__review__page'>
            <div className="d-flex align-items-center mb-4 px-md-3 pt-4">
                <button 
                    onClick={handleGoBack} 
                    className="btn btn-light d-flex align-items-center shadow-sm" 
                    style={{ 
                        borderRadius: '20px', 
                        padding: '6px 16px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #E0E4EC',
                        color: '#19064B',
                        fontWeight: '500',
                        fontSize: '13px',
                        gap: '6px'
                    }}
                >
                    <FaAngleLeft size={13} />
                    <span>Go back</span>
                </button>
            </div>
    
            <section><CardsProgressSteps currentStep={5} /></section>
            
    
            <section className="create-card_section review__card__section pt-4">
                <div className="container">
                    <div className='create_card_header'>
                        {/* <div className="d-flex align-items-center gap-3 ps-1 ps-md-4">
                            <img src={chooseCardIcon} alt="icon" width="30px" height="30px" className='img-fluid' />
                            <h4 className='mb-0'>Card Review</h4>
                        </div> */}
                    </div>
    
                    <div className="card__review__details">
                        <div className="card__details__display row">
                            <div className="col-md-6 col-sm-12 my-3 d-flex flex-column gap-4"> 
                                <div className={`card__details__preview ${cardDetails?.backgroundColor === '#F5F5F5' && 'dark-logo'} mb-1`} 
                                    style={{
                                        backgroundColor: cardDetails?.backgroundColor,
                                        backgroundImage: `url(${storedCardDetails?.cardTheme?.bgImage})`,
                                    }}>
                                        <div className="card__left__details">
                                            <h4>{cardDetails?.nickname}</h4>
 
                                            <div className="">
                                                <h5>{cardDetails?.cardholderName}</h5>
                                                <h6 className="mt-1">{cardDetails?.cardNumber}</h6>
                                            </div>
                                        </div>
                                        <div className="card__right__details">
                                            <img src={brandLogo2} alt="Card Logo" width="41.59px" height='35px' />
                                            <div className="">
                                                <span className='d-flex justify-content-end' style={{ fontSize: '7px' }}>debit</span>
                                                <img src={cardDetails?.cardLogo} alt="Card Type" className='img-fluid' />
                                            </div>
                                        </div>
                                </div>

                                {storedCardDetails?.accountPurpose && (
                                    <div className="transaction-details p-4" style={{ backgroundColor: '#F8F9FA', borderRadius: '16px', border: '1px solid #f1f3f5' }}>
                                        <h6 className="fw-semibold mb-3 pb-3 border-bottom" style={{ color: '#19064B', fontSize: '15px' }}> Additional Details </h6>
                                        <div className="d-flex flex-column gap-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted fw-medium" style={{ fontSize: '13px' }}> Purpose of Card Creation </span>
                                                <span className="fw-semibold text-end" style={{ fontSize: '13px', color: '#19064B', maxWidth: '60%' }}>
                                                    {formatLabel(storedCardDetails?.accountPurpose)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted fw-medium" style={{ fontSize: '13px' }}> Expected Monthly Spending Amount </span>
                                                <span className="fw-semibold text-end" style={{ fontSize: '13px', color: '#19064B', maxWidth: '60%' }}>
                                                    {storedCardDetails?.cardCurrency}{getFormattedAmount(storedCardDetails?.expectedMonthlyVolume)}
                                                </span>
                                            </div>
                                            {/* <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted fw-medium" style={{ fontSize: '13px' }}>Street</span>
                                                <span className="fw-semibold text-end" style={{ fontSize: '13px', color: '#19064B', maxWidth: '60%' }}>{creationPayload.address}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted fw-medium" style={{ fontSize: '13px' }}>City, State</span>
                                                <span className="fw-semibold text-end" style={{ fontSize: '13px', color: '#19064B', maxWidth: '60%' }}>{creationPayload.city}, {creationPayload.state}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted fw-medium" style={{ fontSize: '13px' }}>Postal Code</span>
                                                <span className="fw-semibold text-end" style={{ fontSize: '13px', color: '#19064B', maxWidth: '60%' }}>{creationPayload.postal_code}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted fw-medium" style={{ fontSize: '13px' }}>Country Code</span>
                                                <span className="fw-semibold text-end" style={{ fontSize: '13px', color: '#19064B', maxWidth: '60%' }}>{creationPayload.iso2_country_code}</span>
                                            </div> */}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6 col-sm-12 my-3">
                                <div className="card-operation-summary mt-0">
                                    <div className="card-operation-summary-header">
                                        <div className="card-operation-summary-icon" aria-hidden="true">
                                            <i className="fa-solid fa-receipt"></i>
                                        </div>
                                        <h6>Your card will be funded with</h6>
                                        {/* <p>Your card will be funded with</p> */}
                                        <div className="card-operation-summary-amount">
                                            <strong>${parseFloat(amountFunded || 0).toFixed(2)}</strong>
                                        </div>
                                    </div>
 
                                    <div className="card-operation-summary-details">
                                        <div className="card-operation-summary-row">
                                            <span>You're funding</span>
                                            <strong className="card-operation-summary-card">
                                                {storedCardDetails?.nickname && <strong>{storedCardDetails.nickname}</strong>}
                                                <small>{getMaskedCardNumber()}</small>
                                            </strong>
                                        </div>
 
                                        {getExchangeRateDisplay() && (
                                            <div className="card-operation-summary-row">
                                                <span>Exchange Rate Info</span>
                                                <strong>{getExchangeRateDisplay()}</strong>
                                            </div>
                                        )}
 
                                        <div className="card-operation-summary-row">
                                            <span>Paying with</span>
                                            <strong className="card-operation-summary-wallet">
                                                <strong>Your Wallet</strong>
                                                <small>Balance: {formatWalletCurrency(walletBalance)}</small>
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="terms-text mt-2">
                                        <p>By clicking create, I authorize Cardplot Ltd to initiate the transaction above and also agree to the stipulated 
                                            <Link href="#">Terms and Conditions</Link> for creating virtual card
                                        </p>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center mt-1">
                                        <button onClick={handleContinue} disabled={loading} className="btn w-100 py-3 fw-semibold text-white shadow-sm" style={{ backgroundColor: '#0A95FF', borderRadius: '14px', fontSize: '15px', transition: 'all 0.3s' }}>
                                            {loading ? (
                                                <><span className="spinner-border spinner-border-sm me-2"></span>Creating...</>
                                            ) : "Create Virtual Card"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        </section>
    </section>
  )
}

export default CardReview;
